// Blog posts: careers and AI. Same block format as blogPosts.js
// (rendered by components/ContentBlocks).

export const careerAIPosts = [
  {
    slug: 'service-to-product-company-switch',
    title: 'From Service Company to Product Company: A Practical Switch Plan',
    excerpt: 'Why the switch feels hard, what product companies actually test, and a 16-week plan you can follow around a full-time job.',
    category: 'Career',
    tags: ['Career Switch', 'Product Companies', 'Interviews'],
    author: 'Codeazy Team',
    date: '2026-10-29',
    content: [
      { type: 'p', text: 'Thousands of engineers start their careers at IT services companies, and many of them eventually want to move to product companies: teams that build and own a product, ship to millions of users, and usually pay more. The switch is very doable, but it rarely happens by accident. It needs a plan that fits around a demanding job.' },
      { type: 'p', text: 'This guide explains what is genuinely different about product company interviews, how to close the gap, and how to tell your story so your service experience works for you instead of against you.' },

      { type: 'h2', text: 'Why the switch feels hard' },
      { type: 'p', text: 'It is not because service engineers are less capable. It is because the work and the interviews measure different things.' },
      { type: 'table', headers: ['', 'Typical service project', 'What product interviews test'], rows: [
        ['Problem solving', 'Configuring and extending existing systems', 'Solving new problems under time pressure (DSA rounds)'],
        ['Design', 'Decisions often made by the client or architect', 'You designing systems for scale and failure'],
        ['Ownership', 'Tickets scoped by someone else', 'Owning features end to end, with trade-offs'],
        ['Fundamentals', 'Framework-specific knowledge', 'Language internals, data structures, how the web works']
      ] },
      { type: 'p', text: 'So the gap is mostly practice and exposure, not talent. The good news: every row in that table can be learned deliberately.' },

      { type: 'h2', text: 'What product company interviews look like' },
      { type: 'ol', items: [
        '**Online assessment**: two or three timed coding problems.',
        '**DSA rounds**: one or two live problem-solving interviews where you explain your approach.',
        '**Machine coding or LLD**: build a working feature or design classes for a small system.',
        '**System design (HLD)**: usually from around 3 years of experience; design a real system at scale.',
        '**Project deep-dive**: they pick something from your CV and go five levels deep.',
        '**Hiring manager / behavioural**: ownership, conflict, failure, why you want to move.'
      ] },
      { type: 'callout', variant: 'note', title: 'Experience changes the weight', text: 'With 0–2 years, DSA and fundamentals carry most of the weight. From 3+ years, system design and how you talk about your own work matter as much as coding.' },

      { type: 'h2', text: 'The 16-week plan' },
      { type: 'p', text: 'This assumes about 10–12 hours a week: an hour on most weekdays and longer sessions on weekends. Adjust the pace, not the order.' },
      { type: 'h3', text: 'Weeks 1–2: Find your gaps' },
      { type: 'ul', items: [
        'Pick 10–15 target companies and read their recent job descriptions. Note the common skills.',
        'Solve 10 easy and 5 medium problems to see honestly where you stand.',
        'List every project you have worked on and what you personally did in each.'
      ] },
      { type: 'h3', text: 'Weeks 3–10: DSA by pattern' },
      { type: 'p', text: 'Do not grind random problems. Learn one pattern at a time (hash maps, two pointers, sliding window, binary search, stacks, trees, graphs, heaps, dynamic programming) and solve 8–12 problems per pattern. After each problem, write one line: “I recognised this pattern because…”. That sentence is what makes new problems feel familiar in interviews.' },
      { type: 'tip', text: 'Time-box hard problems to 30–40 minutes. If you are stuck, read the approach (not the code), then implement it yourself the next day.' },
      { type: 'h3', text: 'Weeks 8–14: System design and fundamentals' },
      { type: 'ul', items: [
        'Learn the building blocks: load balancers, caching, databases and indexes, replication, sharding, queues.',
        'Practise 6–8 classic designs: URL shortener, rate limiter, chat, news feed, notification system.',
        'Revise your main language deeply: for Java, collections internals, concurrency and the JVM; for JavaScript, the event loop, closures and promises.'
      ] },
      { type: 'h3', text: 'Weeks 12–16: Mock interviews and applications' },
      { type: 'ul', items: [
        'Do at least 4–6 mock interviews with someone who will give honest feedback.',
        'Rewrite your CV around impact (see below).',
        'Apply in batches: a few “practice” companies first, your dream companies once you are converting interviews.'
      ] },

      { type: 'h2', text: 'Turn your service experience into a strength' },
      { type: 'p', text: 'The biggest mistake is describing service work the way your project manager would. Product interviewers want to hear what problem you solved, what you decided, and what changed because of it.' },
      { type: 'compare', badLabel: 'Weak', bad: `Worked on a banking application
for a US client. Developed REST
APIs using Spring Boot. Fixed
production bugs.`, goodLabel: 'Strong', good: `Built the payment-status APIs
(Spring Boot) used by 2 client
apps; added caching that cut p95
latency from 900 ms to 180 ms.
Led root-cause analysis for a
recurring timeout in production.`, text: 'Same job, very different signal. Use real numbers from your own work; if you do not know them, measure them now.' },
      { type: 'ul', items: [
        'Pick two projects you can discuss for 20 minutes each: the architecture, your part, the hardest bug, what you would change.',
        'Have specific stories for production incidents, performance fixes and working with difficult requirements.',
        'Be ready for “why are you leaving?”: talk about wanting ownership, scale and product impact, never about your employer negatively.'
      ] },

      { type: 'h2', text: 'Managing it alongside a full-time job' },
      { type: 'ul', items: [
        'Protect a fixed daily slot, even 45 minutes. Consistency beats weekend marathons.',
        'Keep a simple tracker: patterns done, problems solved, designs practised.',
        'Use commute or lunch time for revision, not new topics.',
        'Check your notice period early: 60–90 day notice periods affect when you should start applying.'
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Starting system design before you can solve medium DSA problems comfortably.',
        'Applying to your dream company first, before you have had any interview practice.',
        'Collecting certificates instead of practising out loud.',
        'Underselling your experience because it was “only service work”.'
      ] },

      { type: 'h2', text: 'Choosing the right target companies' },
      { type: 'p', text: '“Product company” covers very different places, and they interview differently. Knowing which kind you are targeting changes how you prepare.' },
      { type: 'table', headers: ['Type', 'What they value', 'Interview emphasis'], rows: [
        ['Early-stage startups', 'Speed, ownership, breadth', 'Practical coding, take-home or machine coding, culture fit'],
        ['Growth-stage product companies', 'Solid engineering at growing scale', 'DSA, machine coding or LLD, system design for experienced hires'],
        ['Large tech companies', 'Strong fundamentals, consistency at very large scale', 'Several DSA rounds, system design, structured behavioural rounds'],
        ['Product teams inside enterprises', 'Domain knowledge plus engineering quality', 'Mix of practical coding, design and domain questions']
      ] },
      { type: 'p', text: 'A good strategy is to include a few companies from each tier. Startups and growth-stage companies often move faster and give you real interview practice before you face your top targets.' },

      { type: 'h2', text: 'A realistic weekly schedule' },
      { type: 'table', headers: ['Day', 'Time', 'Focus'], rows: [
        ['Monday–Thursday', '60–75 min', 'Two DSA problems from the current pattern, or one design topic'],
        ['Friday', '30 min', 'Revise the week’s mistakes; no new topics'],
        ['Saturday', '2–3 hours', 'Timed mock: two problems in 75 minutes, or one full system design'],
        ['Sunday', '2 hours', 'Fundamentals, CV and project stories; plan next week']
      ] },
      { type: 'p', text: 'The revision slot matters more than it looks. Re-solving a problem you failed a week earlier, without hints, is one of the fastest ways to make a pattern stick.' },

      { type: 'h2', text: 'Signs you are ready to start applying' },
      { type: 'ul', items: [
        'You solve most medium problems in a pattern you have studied within 30–35 minutes, explaining as you go.',
        'You can design a URL shortener or rate limiter end to end without notes, including trade-offs.',
        'You can talk about each project on your CV for 15 minutes, including what went wrong.',
        'Your mock interview feedback is about polish, not fundamentals.'
      ] },
      { type: 'p', text: 'You do not need to feel 100% ready. Interviewing is itself a skill, and the first few interviews are where you learn the most.' },

      { type: 'h2', text: 'Frequently asked questions' },
      { type: 'qa', question: 'Should I quit my job to prepare full time?', answer: ['Usually no. Preparing alongside your job keeps your income and avoids a gap you will have to explain. A consistent plan of 10–12 hours a week is enough for most people; quitting tends to add pressure rather than speed.'] },
      { type: 'qa', question: 'How many DSA problems do I need to solve?', answer: ['There is no magic number. Covering every major pattern with 8–12 problems each, and being able to explain your approach, matters far more than a high total. Quality and revision beat volume.'] },
      { type: 'qa', question: 'My stack is old (for example, an older Java version or a legacy framework). Is that a problem?', answer: ['It matters less than you think. Interviews focus on fundamentals and problem solving. Do learn the modern version of your main language and one current framework well enough to discuss it, and build one small project with it if you can.'] },

      { type: 'summary', items: [
        'The gap between service and product roles is practice and exposure, not talent.',
        'Learn DSA by pattern, then system design, then do mock interviews.',
        'Rewrite your experience around problems, decisions and measurable impact.',
        'A steady 10–12 hours a week for about 4 months is enough for most engineers.'
      ] }
    ]
  },
  {
    slug: 'first-developer-job-as-a-fresher',
    title: 'No Experience, No Job? How Freshers Actually Land Their First Developer Role',
    excerpt: 'Breaking the “experience required” loop: what to build, how to show it, and where to apply when you have no work experience yet.',
    category: 'Career',
    tags: ['Freshers', 'Job Search', 'Projects'],
    author: 'Codeazy Team',
    date: '2026-10-29',
    content: [
      { type: 'p', text: 'You graduated, you know the basics, and every listing asks for experience you cannot get without a job. It feels like a closed loop. It is not, but getting out of it takes a different approach from sending the same CV to a hundred openings.' },
      { type: 'p', text: 'Employers ask for experience because it is a shortcut for one question: can this person do the job with real code, real teammates and real deadlines? Your goal is to answer that question with evidence, even before your first offer.' },

      { type: 'h2', text: 'Step 1: Replace “experience” with evidence' },
      { type: 'p', text: 'Experience is not only a job title. Anything that shows you have built, shipped and collaborated counts, if you can explain it well.' },
      { type: 'table', headers: ['Evidence', 'What it proves', 'Effort'], rows: [
        ['A deployed project with real users', 'You can ship and maintain software', 'High'],
        ['Work on a team with code reviews (internship, team programme, open source)', 'You can collaborate the way companies do', 'High'],
        ['Solid DSA practice', 'You can pass online assessments', 'Medium'],
        ['Clear GitHub with READMEs', 'You communicate and document', 'Low'],
        ['Certificates', 'You completed a course', 'Low signal on its own']
      ] },

      { type: 'h2', text: 'Step 2: Build one project that stands out' },
      { type: 'p', text: 'Recruiters see the same to-do apps and weather apps every day. One serious project beats five tutorial clones. A strong fresher project:' },
      { type: 'ul', items: [
        'Solves a real problem for real people (your college, a local business, a community you are part of).',
        'Has authentication, a database, and at least one non-trivial feature (search, payments, notifications, file upload).',
        'Is deployed with a live link, and has a README with screenshots, setup steps and architecture notes.',
        'Shows engineering habits: meaningful commits, tests for core logic, environment variables for secrets.',
        'Has users or feedback, even 20 people, which gives you real stories.'
      ] },
      { type: 'tip', text: 'Keep a short “engineering log” while you build: problems you hit, how you debugged them, decisions you made. It becomes your interview material.' },

      { type: 'h2', text: 'Step 3: Learn how teams actually work' },
      { type: 'p', text: 'The gap between a solo project and a job is mostly process. Learn and practise these before your first interview:' },
      { type: 'ul', items: [
        'Git beyond `add` and `commit`: branches, pull requests, rebasing, resolving conflicts.',
        'Code reviews: giving and receiving feedback without taking it personally.',
        'Breaking work into tickets and estimating them.',
        'Basic deployment and debugging with logs.',
        'Using AI coding tools responsibly, and reviewing what they generate.'
      ] },
      { type: 'p', text: 'Open source contributions, team-based programmes and internships are the fastest way to get this exposure, because someone else reviews your work.' },

      { type: 'h2', text: 'Step 4: Make your CV easy to shortlist' },
      { type: 'compare', badLabel: 'Generic', bad: `Skills: HTML, CSS, JS, React,
Node, MongoDB, Python, Java,
C++, AWS, Docker, ML

Project: E-commerce website
using MERN stack.`, goodLabel: 'Specific', good: `Project: CampusSwap (live link)
Marketplace used by 300+ students
to buy and sell used books.
• Built search with MongoDB text
  indexes; results in <200 ms
• Razorpay payments + webhooks
• JWT auth, role-based admin`, text: 'List fewer skills you can defend, and describe projects with users, features and results.' },
      { type: 'ul', items: [
        'One page. Projects above education if your projects are strong.',
        'Every skill listed must survive “tell me about a time you used it”.',
        'Link GitHub and live demos; make sure they work on mobile.'
      ] },

      { type: 'h2', text: 'Step 5: Apply smarter, not more' },
      { type: 'ul', items: [
        'Target 20–30 roles that genuinely match your skills rather than 300 random ones.',
        'Referrals have much higher response rates than cold applications. Message engineers (not just recruiters) with a short note and your project link.',
        'Startups often value demonstrated ability over credentials; do not ignore them.',
        'Track applications in a sheet: company, date, contact, stage, follow-up date.'
      ] },
      { type: 'callout', variant: 'tip', title: 'A referral message that works', text: '“Hi Priya, I saw your team is hiring a junior frontend engineer. I built CampusSwap (React, Node, MongoDB; 300 users): [link]. Would you be open to referring me, or telling me what your team looks for? Happy to share my CV.” Short, specific, easy to say yes to.' },

      { type: 'h2', text: 'Step 6: Convert the interviews you get' },
      { type: 'ul', items: [
        'Practise explaining your project in 2 minutes, then in 10 minutes with architecture and trade-offs.',
        'Prepare for “what would you do differently?” and “what was the hardest bug?”.',
        'Do timed DSA practice: easy and medium problems, explained out loud.',
        'After every rejection, write down which round you lost and why. Fix that specific gap.'
      ] },

      { type: 'h2', text: 'Explaining a gap after graduation' },
      { type: 'p', text: 'If months have passed since you graduated, do not apologise or over-explain. Say briefly what happened and spend the rest of the answer on what you have done since: the project, the skills, the team experience. Recent, real work is the best answer to a gap.' },

      { type: 'h2', text: 'Project ideas that stand out' },
      { type: 'table', headers: ['Idea', 'Why it impresses', 'Stretch feature'], rows: [
        ['Booking system for a local clinic, gym or tutor', 'Real users, real constraints', 'Reminders by email or WhatsApp, calendar sync'],
        ['Expense splitter for hostels or trips', 'Non-trivial logic (balances, settlements)', 'Minimise the number of transactions'],
        ['Second-hand marketplace for your campus', 'Search, images, auth, moderation', 'Payments with webhooks'],
        ['Job tracker with reminders', 'You are the user, so you understand the problem', 'Parse job emails with an LLM'],
        ['Document Q&A for your college notes', 'Shows modern AI skills', 'Citations and evaluation']
      ] },
      { type: 'p', text: 'Whatever you build, the story matters as much as the code: why you built it, who uses it, the hardest bug and what you would change.' },

      { type: 'h2', text: 'A 90-day plan' },
      { type: 'ol', items: [
        '**Days 1–15**: pick one stack (for example MERN or Java + Spring Boot) and fix fundamental gaps. Start one DSA pattern per week.',
        '**Days 15–60**: build and deploy your main project. Get 10–20 real users and fix what they find.',
        '**Days 30–75**: get team experience: open source pull requests, an internship, or a team-based programme where your code is reviewed.',
        '**Days 60–90**: rewrite your CV, practise your project story, do mock interviews and apply to 20–30 well-matched roles with referrals where possible.'
      ] },

      { type: 'h2', text: 'What to do while you wait for replies' },
      { type: 'ul', items: [
        'Keep shipping small improvements to your project and write short posts about what you learned.',
        'Contribute to one open source project consistently instead of many projects once.',
        'Practise one timed DSA problem a day so you are sharp when an assessment arrives.',
        'Follow up politely on applications after a week, once.'
      ] },
      { type: 'callout', variant: 'note', title: 'Rejections are normal', text: 'Almost everyone faces many rejections before their first offer. What separates people who get hired is that they learn which round they are losing and fix that specific gap.' },

      { type: 'h2', text: 'Frequently asked questions' },
      { type: 'qa', question: 'Are certificates useless?', answer: ['Not useless, but weak on their own. They show you completed a course. Pair them with a project that uses what you learned, and recruiters have something real to evaluate.'] },
      { type: 'qa', question: 'Should I learn many technologies to qualify for more jobs?', answer: ['No. Depth in one stack plus strong fundamentals beats a long list of tools you cannot discuss. Interviewers will pick any skill on your CV and go deep.'] },
      { type: 'qa', question: 'Is it worth applying to roles that ask for 1–2 years of experience?', answer: ['Often yes, if the rest of the description matches you well. Experience requirements are frequently flexible for candidates who can show real projects and team experience. Use a referral if you can.'] },

      { type: 'summary', items: [
        'Employers ask for experience as a shortcut; answer it with evidence.',
        'One serious, deployed project with users beats many tutorial clones.',
        'Learn team practices: Git workflows, code reviews, tickets and deployments.',
        'Apply to fewer, better-matched roles and use referrals.',
        'Treat every rejection as data about the round you need to fix.'
      ] }
    ]
  },
  {
    slug: 'will-ai-replace-software-engineers',
    title: 'Will AI Replace Software Engineers? What’s Actually Changing',
    excerpt: 'AI is changing how software is written. Here is what is getting automated, what is becoming more valuable, and how to stay ahead.',
    category: 'Career',
    tags: ['AI', 'Career', 'Future of Work'],
    author: 'Codeazy Team',
    date: '2026-10-29',
    content: [
      { type: 'p', text: 'Every few weeks a new AI tool writes more code than the last one, and the same question comes back: is it still worth becoming a software engineer? The honest answer is that the job is changing quickly, but it is not disappearing. What is changing is which parts of the job matter most.' },

      { type: 'h2', text: 'What AI is genuinely good at today' },
      { type: 'ul', items: [
        'Writing boilerplate and common patterns quickly: CRUD endpoints, forms, tests, config.',
        'Explaining unfamiliar code and suggesting fixes for clear error messages.',
        'Translating between languages and frameworks.',
        'Generating first drafts of documentation, test cases and scripts.',
        'Working through well-defined tasks in a codebase when guided by a developer.'
      ] },

      { type: 'h2', text: 'What still needs engineers' },
      { type: 'ul', items: [
        '**Deciding what to build**: understanding users, constraints and trade-offs.',
        '**Design and architecture**: how systems fit together, scale, fail and evolve.',
        '**Judgement**: knowing when generated code is subtly wrong, insecure or unmaintainable.',
        '**Ownership**: being accountable for production systems, incidents and data.',
        '**Communication**: aligning with product, design and other teams.'
      ] },
      { type: 'p', text: 'In other words, writing code is becoming cheaper, while reviewing, designing and owning code is becoming relatively more valuable.' },

      { type: 'h2', text: 'How the work is changing' },
      { type: 'table', headers: ['Before', 'Increasingly'], rows: [
        ['Typing most code by hand', 'Directing AI and reviewing its output'],
        ['Searching docs and forums for answers', 'Asking an assistant, then verifying'],
        ['Juniors learn by writing boilerplate', 'Juniors must learn fundamentals deliberately to judge AI output'],
        ['Features take days to prototype', 'Prototypes take hours; quality and integration take the time'],
        ['AI as a separate field', 'LLM features inside ordinary products']
      ] },

      { type: 'h2', text: 'The real risk' },
      { type: 'p', text: 'The risk is not that AI takes every engineering job. It is that engineers who only know how to produce code that AI can now produce get squeezed, while engineers who combine fundamentals with AI tools become much more productive. The gap between those two groups is growing.' },
      { type: 'callout', variant: 'warning', title: 'The shortcut trap', text: 'If AI writes code you do not understand, you cannot debug it, secure it or extend it. Use AI to go faster at things you understand, and to learn things you don’t, not to skip learning.' },

      { type: 'h2', text: 'How to stay ahead' },
      { type: 'ol', items: [
        '**Double down on fundamentals**: data structures, how the web and databases work, system design. They are what let you judge AI output.',
        '**Make AI tools part of your daily workflow**: assistants in your editor, agents for multi-step tasks. Learn their strengths and failure modes.',
        '**Practise reviewing**: read generated code like a senior engineer reviewing a pull request: correctness, edge cases, security, performance.',
        '**Learn to build with LLMs**: retrieval (RAG), tool use, evaluation and cost control are becoming standard product skills.',
        '**Own outcomes**: ship things end to end and be able to explain why they work.'
      ] },

      { type: 'h2', text: 'What this means for students and freshers' },
      { type: 'p', text: 'Entry-level work is changing the most, because AI handles many of the simple tasks juniors used to learn on. That makes demonstrated fundamentals and real project experience more important, not less. A fresher who can explain their design decisions and catch an AI’s mistake stands out immediately.' },

      { type: 'h2', text: 'Skills that are losing and gaining value' },
      { type: 'table', headers: ['Becoming less differentiating', 'Becoming more valuable'], rows: [
        ['Memorising syntax and APIs', 'Understanding how systems work end to end'],
        ['Writing boilerplate quickly', 'Reviewing code critically, including AI-generated code'],
        ['Knowing one framework deeply but narrowly', 'Fundamentals that transfer: data, networking, design'],
        ['Following detailed specs', 'Turning vague problems into clear plans'],
        ['Working alone on isolated tasks', 'Communication, ownership and judgement in a team'],
        ['Avoiding AI tools', 'Using AI tools fluently and safely']
      ] },

      { type: 'h2', text: 'A 30-day plan to become AI-fluent' },
      { type: 'ol', items: [
        '**Week 1**: use an AI assistant for everyday tasks in your editor. Keep notes on where it helped and where it was wrong.',
        '**Week 2**: practise reviewing. Ask it to implement small features, then find the bugs, missing edge cases and security issues yourself.',
        '**Week 3**: build a small LLM feature: a document Q&A or a summariser with structured output and validation.',
        '**Week 4**: add tool use to it (let the model call one of your functions), and write a small evaluation set to measure quality.'
      ] },
      { type: 'p', text: 'By the end you will understand both sides: using AI to build software, and building software that uses AI. Many teams now expect both.' },

      { type: 'h2', text: 'How interviews are changing' },
      { type: 'ul', items: [
        'More questions about how you would use AI tools on the job, and how you verify their output.',
        'LLM features appearing in system design rounds: retrieval, caching responses, cost and latency.',
        'Greater emphasis on reasoning out loud and debugging, which AI cannot do for you in the room.',
        'Fundamentals still tested directly; some companies explicitly test without AI assistance.'
      ] },

      { type: 'h2', text: 'Frequently asked questions' },
      { type: 'qa', question: 'Is it still worth starting a career in software in 2026?', answer: ['Yes, if you approach it the right way. Software keeps spreading into every industry, and AI creates new kinds of software to build. The bar for entry-level engineers is rising, so focus on fundamentals, real projects and AI fluency rather than only syntax.'] },
      { type: 'qa', question: 'Should I switch to becoming an AI or ML engineer instead?', answer: ['You do not have to choose. Most AI work in companies is software engineering: integrating models, building data pipelines, evaluation and production reliability. Strong software engineers who learn to build with LLMs are in a very good position.'] },
      { type: 'qa', question: 'Will I need to learn advanced maths?', answer: ['For building products with existing models, not much. For training models or research roles, yes: linear algebra, probability and optimisation become important.'] },

      { type: 'summary', items: [
        'AI is automating code writing, not engineering judgement.',
        'Design, review, ownership and communication are becoming more valuable.',
        'Use AI to go faster at what you understand, never to skip understanding.',
        'Learn to build LLM features; they are becoming part of everyday products.'
      ] }
    ]
  },
  {
    slug: 'coding-with-ai-assistants',
    title: 'How to Code with AI Assistants Without Losing Your Skills',
    excerpt: 'A practical workflow for using AI coding tools well: planning, prompting, reviewing and testing, plus the mistakes that slow teams down.',
    category: 'Agentic AI',
    tags: ['AI Tools', 'Productivity', 'Code Review'],
    author: 'Codeazy Team',
    date: '2026-10-29',
    content: [
      { type: 'p', text: 'AI coding assistants can make you dramatically faster, or they can fill your codebase with plausible-looking bugs. The difference is not the tool. It is the workflow around it. Here is how strong engineers use these tools day to day.' },

      { type: 'h2', text: 'Kinds of AI coding tools' },
      { type: 'table', headers: ['Type', 'What it does', 'Best for'], rows: [
        ['Inline completion', 'Suggests the next lines as you type', 'Boilerplate, repetitive code'],
        ['Chat in the editor', 'Answers questions about selected code', 'Explanations, small refactors, debugging'],
        ['Agents', 'Read the codebase, edit several files, run commands and tests', 'Multi-step tasks with clear goals'],
        ['Review assistants', 'Comment on pull requests', 'A second pair of eyes, not a replacement for review']
      ] },

      { type: 'h2', text: 'A workflow that works' },
      { type: 'h3', text: '1. Plan before you prompt' },
      { type: 'p', text: 'Write down what you want in two or three sentences: the goal, the constraints, and how you will know it works. Vague requests produce vague code. If you cannot describe the task clearly, the AI cannot either.' },
      { type: 'h3', text: '2. Give context, not just instructions' },
      { type: 'compare', badLabel: 'Vague', bad: `Add pagination to the API.`, goodLabel: 'Specific', good: `Add cursor-based pagination to
GET /orders (routes/orders.js).
Use createdAt + _id as the cursor,
default limit 20, max 100.
Follow the error format in
middleware/errors.js and add
tests like tests/users.test.js.`, text: 'Point to files, conventions and examples so the output fits your codebase.' },
      { type: 'h3', text: '3. Work in small steps' },
      { type: 'p', text: 'Ask for one change at a time and check it before moving on. Large, multi-file generations are where subtle mistakes hide. Commit often so you can roll back a bad step.' },
      { type: 'h3', text: '4. Review like it is a teammate’s pull request' },
      { type: 'ul', items: [
        'Does it actually solve the problem, including edge cases (empty input, errors, concurrency)?',
        'Is anything invented: functions, packages or API options that do not exist?',
        'Is it secure: input validation, authorisation checks, no secrets in code?',
        'Does it match the codebase’s patterns, or introduce a second way of doing things?',
        'Would you be able to debug this at 2 a.m. during an incident?'
      ] },
      { type: 'h3', text: '5. Make tests the source of truth' },
      { type: 'p', text: 'Ask the assistant to write tests first or alongside the code, then run them. Tests turn “looks right” into “is right”, and they catch regressions when the AI changes something later.' },

      { type: 'h2', text: 'Where AI assistants shine' },
      { type: 'ul', items: [
        'Understanding an unfamiliar codebase: “walk me through how a request reaches the database”.',
        'Writing tests for existing code.',
        'Refactors with clear rules: renaming, extracting functions, migrating APIs.',
        'Scripts and one-off tools.',
        'Explaining error messages and suggesting debugging steps.'
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Accepting large changes without reading them.',
        'Letting the assistant pick dependencies you have not checked.',
        'Pasting secrets, customer data or proprietary code into tools your company has not approved.',
        'Using AI to avoid learning: if you never understand the code, you cannot grow beyond the tool.',
        'Arguing with the model for twenty minutes instead of writing the five lines yourself.'
      ] },
      { type: 'callout', variant: 'warning', title: 'Check your company’s policy', text: 'Many companies restrict which AI tools may see their code. Use approved tools and settings, and never paste credentials or personal data into prompts.' },

      { type: 'h2', text: 'Keep your skills sharp' },
      { type: 'ul', items: [
        'Solve some problems without assistance, especially when learning something new.',
        'When the AI writes something you did not know, look it up and understand it.',
        'Explain generated code to yourself or a teammate before merging it.',
        'Practise interviews without AI; they still test what you know.'
      ] },

      { type: 'h2', text: 'Prompt patterns that work' },
      { type: 'table', headers: ['Pattern', 'Example'], rows: [
        ['Explain first', '“Explain how authentication works in this codebase before changing anything.”'],
        ['Plan, then implement', '“Propose a plan for adding refunds. Don’t write code yet.” Review the plan, then ask for step 1.'],
        ['Tests first', '“Write failing tests for these cases, then implement until they pass.”'],
        ['Constrain', '“Use the existing `db` helper and error format. No new dependencies.”'],
        ['Critique', '“Review this function for edge cases, security issues and performance problems.”'],
        ['Explain the diff', '“Summarise what you changed and why, file by file.”']
      ] },

      { type: 'h2', text: 'A review in practice' },
      { type: 'p', text: 'Here is the kind of code an assistant can produce when asked to “add a search endpoint”, and what a careful review catches.' },
      { type: 'compare', badLabel: 'Generated', bad: `app.get("/search", async (req, res) => {
  const q = req.query.q;
  const rows = await db.query(
    "SELECT * FROM products WHERE name LIKE '%" + q + "%'"
  );
  res.json(rows);
});`, goodLabel: 'After review', good: `app.get("/search", async (req, res) => {
  const q = String(req.query.q || "").trim();
  if (q.length < 2) return res.json([]);
  const rows = await db.query(
    "SELECT id, name, price FROM products " +
    "WHERE name ILIKE $1 ORDER BY name LIMIT 20",
    [\`%\${q}%\`]
  );
  res.json(rows);
});`, text: 'The review fixed SQL injection (string concatenation), unbounded results, over-fetching columns and missing input validation. It works either way in a quick demo; only one version is safe in production.' },

      { type: 'h2', text: 'Ground rules for teams' },
      { type: 'ul', items: [
        'The person who opens the pull request owns the code, however it was written.',
        'AI-generated changes go through the same review, tests and CI as any other change.',
        'Use approved tools and settings; keep secrets and personal data out of prompts.',
        'Prefer small, reviewable pull requests over large AI-generated rewrites.',
        'Share prompts and workflows that work well, the same way you share scripts.'
      ] },

      { type: 'h2', text: 'Frequently asked questions' },
      { type: 'qa', question: 'Should beginners use AI coding assistants?', answer: ['Yes, but carefully. Use them to explain concepts and errors, and to check your work. Write core logic yourself while learning, then compare with what the AI suggests. Understanding has to come first.'] },
      { type: 'qa', question: 'Do AI assistants make code reviews unnecessary?', answer: ['No. They make reviews more important, because more code is produced faster. AI review tools can catch some issues, but they are a second pair of eyes, not a replacement for a human who understands the system.'] },
      { type: 'qa', question: 'How do I stop the AI from inventing APIs?', answer: ['Give it the relevant files and documentation in context, ask it to only use functions that exist in the codebase, and run the code and tests. Anything you cannot find in the docs should be treated as suspect.'] },

      { type: 'summary', items: [
        'Plan first, give specific context, and work in small steps.',
        'Review AI output as strictly as a teammate’s pull request.',
        'Let tests, not appearances, decide whether code is correct.',
        'Use AI to accelerate learning, not to replace it.'
      ] }
    ]
  },
  {
    slug: 'build-your-first-rag-app',
    title: 'Build Your First RAG App: Chat With Your Own Documents',
    excerpt: 'A step-by-step guide to retrieval-augmented generation: chunking, embeddings, retrieval, prompting and evaluation, with code.',
    category: 'Agentic AI',
    tags: ['RAG', 'LLMs', 'Embeddings'],
    author: 'Codeazy Team',
    date: '2026-10-29',
    content: [
      { type: 'p', text: 'Large language models know a lot, but they do not know your documents: your company handbook, your product docs, your course notes. Retrieval-augmented generation (RAG) fixes that by finding the relevant pieces of your documents and giving them to the model along with the question. It is the most common pattern in production LLM apps.' },
      { type: 'p', text: 'In this guide we build a small RAG pipeline step by step. The code uses JavaScript and stands in generic `embed()` and `llm.generate()` functions for whichever model provider you use.' },

      { type: 'h2', text: 'How RAG works' },
      { type: 'ol', items: [
        '**Ingest**: split documents into chunks and turn each chunk into an embedding (a vector of numbers that captures meaning).',
        '**Store**: save the vectors with the original text and metadata.',
        '**Retrieve**: embed the user’s question and find the chunks with the most similar vectors.',
        '**Generate**: give the model the question plus those chunks, and ask it to answer only from them.'
      ] },

      { type: 'h2', text: 'Step 1: Chunk your documents' },
      { type: 'p', text: 'Chunks that are too large dilute relevance; chunks that are too small lose context. Splitting on structure (headings and paragraphs) usually beats splitting every N characters.' },
      { type: 'code', language: 'javascript', code: `function chunkByParagraphs(text, maxChars = 1200, overlap = 1) {
  const paragraphs = text.split(/\\n\\s*\\n/).map((p) => p.trim()).filter(Boolean);
  const chunks = [];
  let current = [];

  for (const p of paragraphs) {
    const size = current.join("\\n\\n").length + p.length;
    if (current.length && size > maxChars) {
      chunks.push(current.join("\\n\\n"));
      current = current.slice(-overlap);     // keep the last paragraph for context
    }
    current.push(p);
  }
  if (current.length) chunks.push(current.join("\\n\\n"));
  return chunks;
}

const doc = "Refunds\\n\\nYou can request a refund within 7 days.\\n\\nCertificates\\n\\nIssued after the final project.";
console.log(chunkByParagraphs(doc, 60));`, output: `[
  'Refunds\\n\\nYou can request a refund within 7 days.\\n\\nCertificates',
  'Certificates\\n\\nIssued after the final project.'
]` },
      { type: 'tip', text: 'Store metadata with every chunk: document title, section heading, URL and last-updated date. You will need it for filtering, citations and freshness.' },

      { type: 'h2', text: 'Step 2: Embed and store' },
      { type: 'code', language: 'javascript', code: `const store = [];   // swap for pgvector, OpenSearch or a vector database later

async function ingest(doc) {
  const chunks = chunkByParagraphs(doc.text);
  const vectors = await embed(chunks);            // one vector per chunk
  chunks.forEach((text, i) => {
    store.push({ id: \`\${doc.id}#\${i}\`, text, vector: vectors[i], title: doc.title, url: doc.url });
  });
}`, output: `` },
      { type: 'p', text: 'For a few thousand chunks, an in-memory array or Postgres with the pgvector extension is plenty. Dedicated vector databases help when you have millions of chunks or need advanced filtering at scale.' },

      { type: 'h2', text: 'Step 3: Retrieve the most relevant chunks' },
      { type: 'code', language: 'javascript', code: `function cosine(a, b) {
  let dot = 0, na = 0, nb = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i]; na += a[i] * a[i]; nb += b[i] * b[i];
  }
  return dot / (Math.sqrt(na) * Math.sqrt(nb));
}

async function retrieve(question, k = 5) {
  const [q] = await embed([question]);
  return store
    .map((chunk) => ({ ...chunk, score: cosine(q, chunk.vector) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, k);
}

console.log(cosine([1, 0], [1, 0]), cosine([1, 0], [0, 1]).toFixed(1));`, output: `1 0.0` },
      { type: 'p', text: 'Pure vector search can miss exact terms like error codes, product names or IDs. Combining it with keyword search (hybrid search) and then reranking the top candidates usually gives a big quality jump.' },

      { type: 'h2', text: 'Step 4: Generate a grounded answer' },
      { type: 'code', language: 'javascript', code: `async function answer(question) {
  const chunks = await retrieve(question, 5);
  const context = chunks.map((c, i) => \`[\${i + 1}] \${c.title}\\n\${c.text}\`).join("\\n\\n");

  return llm.generate({
    system: "Answer using only the context. Cite sources like [1]. " +
            "If the context does not contain the answer, say you don't know.",
    prompt: \`<context>\\n\${context}\\n</context>\\n\\nQuestion: \${question}\`,
    temperature: 0.2
  });
}`, output: `` },
      { type: 'ul', items: [
        'Telling the model it may say “I don’t know” reduces made-up answers.',
        'Citations let users verify answers and make errors easy to spot.',
        'Low temperature keeps factual answers consistent.'
      ] },

      { type: 'h2', text: 'Step 5: Evaluate, then improve' },
      { type: 'p', text: 'Create 30–50 real questions with the document section that answers each. Measure two things separately:' },
      { type: 'table', headers: ['What to check', 'How', 'Typical fix'], rows: [
        ['Retrieval: is the right chunk in the top 5?', 'Compare retrieved chunk ids to the expected section', 'Better chunking, hybrid search, reranking, query rewriting'],
        ['Generation: is the answer correct and grounded?', 'Human review, or an LLM judge with a clear rubric', 'Clearer instructions, fewer but better chunks, citations']
      ] },
      { type: 'callout', variant: 'note', title: 'Most bad answers are retrieval problems', text: 'If the right information never reaches the model, no prompt can fix the answer. Check retrieval first.' },

      { type: 'h2', text: 'Taking it to production' },
      { type: 'ul', items: [
        '**Permissions**: filter chunks by what the user is allowed to see before retrieval, not after.',
        '**Freshness**: re-index documents when they change, and delete chunks from removed documents.',
        '**Prompt injection**: retrieved documents can contain instructions; treat them as data and limit what the model can do.',
        '**Cost and latency**: cache embeddings, keep context focused, and stream answers.',
        '**Monitoring**: log questions, retrieved chunks and user feedback to find gaps.'
      ] },

      { type: 'h2', text: 'Choosing a chunk size' },
      { type: 'table', headers: ['Content', 'Starting point', 'Why'], rows: [
        ['FAQs and short answers', 'One question and answer per chunk', 'Each chunk is a complete, self-contained answer'],
        ['Documentation and articles', 'One section (~300–800 tokens) with a small overlap', 'Keeps headings and explanations together'],
        ['Long reports and contracts', 'Paragraph groups plus the section title prepended', 'Chunks stay meaningful out of context'],
        ['Code', 'One function or class per chunk', 'Matches how people ask about code']
      ] },
      { type: 'tip', text: 'Prepend the document title and section heading to each chunk’s text before embedding. A chunk that says “Refunds are allowed within 7 days” retrieves much better when it also says it comes from “Course Policies › Refunds”.' },

      { type: 'h2', text: 'Hybrid search with reciprocal rank fusion' },
      { type: 'p', text: 'Run vector search and keyword search separately, then merge the two ranked lists. Reciprocal rank fusion (RRF) is a simple, robust way to merge: each result scores 1 / (k + rank) in every list it appears in.' },
      { type: 'code', language: 'javascript', code: `function reciprocalRankFusion(lists, k = 60) {
  const scores = new Map();
  for (const list of lists) {
    list.forEach((id, index) => {
      scores.set(id, (scores.get(id) ?? 0) + 1 / (k + index + 1));
    });
  }
  return [...scores.entries()].sort((a, b) => b[1] - a[1]).map(([id]) => id);
}

const vectorResults = ["refunds#2", "certificates#1", "refunds#1"];
const keywordResults = ["refunds#1", "refunds#2", "pricing#3"];
console.log(reciprocalRankFusion([vectorResults, keywordResults]));`, output: `[ 'refunds#2', 'refunds#1', 'certificates#1', 'pricing#3' ]` },
      { type: 'p', text: 'Results that rank well in both lists rise to the top. Then send the top 20–30 to a reranker and keep the best 5–6 for the prompt.' },

      { type: 'h2', text: 'Follow-up questions in a chat' },
      { type: 'p', text: 'In a conversation, users ask things like “and what about certificates?”. Embedding that alone retrieves poorly. Ask the model to rewrite the latest message into a standalone question using the chat history (“What is the policy for certificates in the full-stack course?”), and retrieve with the rewritten question.' },

      { type: 'h2', text: 'Frequently asked questions' },
      { type: 'qa', question: 'Do I need a vector database to start?', answer: ['No. For a prototype or a few thousand chunks, an in-memory array or Postgres with pgvector is enough. Move to a dedicated vector database when scale or filtering needs justify it.'] },
      { type: 'qa', question: 'Why not just put all my documents in the prompt?', answer: ['Long contexts cost more, respond slower, and models attend less reliably to details buried in very long inputs. Retrieval keeps prompts small and focused, and lets you cite sources.'] },
      { type: 'qa', question: 'Should I fine-tune instead of using RAG?', answer: ['For knowledge that changes or must be cited, RAG is the better fit. Fine-tuning is for teaching style, format or specialised behaviour, and it is expensive to repeat when facts change.'] },

      { type: 'summary', items: [
        'RAG = chunk, embed, retrieve, then generate from the retrieved context.',
        'Chunk on structure and keep metadata for filtering and citations.',
        'Hybrid search plus reranking beats vector search alone.',
        'Evaluate retrieval and generation separately; most failures are retrieval.',
        'Enforce permissions and treat retrieved text as untrusted data.'
      ] }
    ]
  }
];
